import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { SnackBarService } from "@services/snack-bar.service";
import { ConfirmActionModalComponent } from "@shared/modals/confirm-action-modal/confirm-action-modal.component";
import { take } from "rxjs";
import { ButtonComponent } from "../../../../../../shared/components/button/button.component";
import { TicketBatch } from "../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { eventHasPaidSales } from "../../../../../../shared/helpers/ticket-sales-lock.helper";
import { EventTypeGroup } from "../../event-form.form";
import {
  EVENT_VISIBILITY_RULES,
  EventVisibilityRule,
} from "./event-type-rules";

@Component({
  selector: "app-event-type-step",
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: "./event-type-step.component.html",
  styleUrl: "./event-type-step.component.scss",
})
export class EventTypeStepComponent {
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackBarService);

  @Input({ required: true }) formGroup!: FormGroup<EventTypeGroup>;
  @Input({ required: true }) isPrivateControl!: FormControl<boolean>;
  @Input() eventId?: string;
  @Input() ticketBatches: TicketBatch[] = [];
  @Output() ticketBatchesChange = new EventEmitter<TicketBatch[]>();

  get isEventTypeLocked(): boolean {
    return !!this.eventId && eventHasPaidSales(this.ticketBatches);
  }

  get isPaid(): boolean {
    return this.formGroup.controls.isEventPaid.value;
  }

  get isPrivate(): boolean {
    return this.isPrivateControl.value;
  }

  get currentRule(): EventVisibilityRule {
    const type = this.isPaid ? "paid" : "free";
    const visibility = this.isPrivate ? "private" : "public";
    return EVENT_VISIBILITY_RULES[`${type}_${visibility}`];
  }

  setVisibility(isPrivate: boolean): void {
    this.isPrivateControl.setValue(isPrivate);
  }

  handleChangeEventType(isPaidEvent: boolean) {
    if (this.formGroup.controls.isEventPaid.value === isPaidEvent) {
      return;
    }

    if (this.isEventTypeLocked) {
      this.snackbarService.openSnackBar(
        "Não é possível alterar o tipo de evento após o início das vendas.",
        "Fechar",
        "warning",
      );
      return;
    }

    if (this.ticketBatches.length > 0) {
      const dialogRef = this.dialog.open(ConfirmActionModalComponent, {
        width: "500px",
        data: {
          modalTitle: "Atenção",
          modalContent: isPaidEvent
            ? "Os ingressos existentes serão mantidos. Depois você poderá definir o valor de cada um. Deseja continuar?"
            : "Os ingressos existentes serão mantidos e os valores serão zerados. Deseja continuar?",
          confirmMessage: "Confirmar",
          cancelMessage: "Cancelar",
        },
      });

      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe((response) => {
          if (response) {
            this.applyEventTypeChange(isPaidEvent);
          }
        });
      return;
    }

    this.applyEventTypeChange(isPaidEvent);
  }

  private applyEventTypeChange(isPaidEvent: boolean): void {
    this.formGroup.controls.isEventPaid.setValue(isPaidEvent);
    if (isPaidEvent || !this.ticketBatches.length) {
      return;
    }

    this.ticketBatchesChange.emit(
      this.ticketBatches.map((batch) => ({
        ...batch,
        tickets: (batch.tickets ?? []).map((ticket) => ({
          ...ticket,
          price: 0,
        })),
      })),
    );
  }
}
